import { cloneDeep } from 'lodash';
import { trackConfig } from '@/framework';
import { Layer } from '@/layers/layer';
import {
    LayerType,
    MaterialIdsType,
    PluginType,
    type IExportPlugin,
    type ILayerProps,
    type IMarkupLayerProps,
    type IMarkupPoint,
    type ISize,
    type MarkupDasharrayType,
    type MarkupLabelType,
    type MarkupLabelUnit,
    type MarkupLinecapType,
    type MarkupTextPositionType,
} from '@/version-types';
import markupLayerBehavior from './behavior';
import { prepareMarkupLayerDocumentFonts } from './font-measurement';
import {
    clampFontSize,
    fixDrawResult,
    fixLabelSize,
    LabelKit,
    setFillColor,
    setFillStyle,
    setFillWidth,
    setLabelBackground,
    setLabelColor,
    setLabelPosition,
    setLabelRotate,
    setLine,
    setPoint,
    setSwap,
    updateScreenshotUrl,
} from './utils';

export * from './utils';
export * from './config';
export { MarkupDrawPlugin, markupDrawPlugin } from './plugins/draw';

// 初始化，字体丢失，修正图层宽高
export function fixMarkupLayerSize(layer: ILayerProps) {
    if (!fixLabelSize(layer)) return false;
    fixDrawResult(layer, cloneDeep(layer));
    return true;
}

/** 标注图层 */
export class MarkupLayer extends Layer {
    /** 用于属性变更计算的原始图层数据 */
    private operateOriginLayer?: ILayerProps | null = null;
    private labelOperationVersions = new Map<string, number>();

    constructor(identity: string) {
        super(identity);
    }

    private beginLabelOperation(layerId: string) {
        const version = (this.labelOperationVersions.get(layerId) || 0) + 1;
        this.labelOperationVersions.set(layerId, version);
        return version;
    }

    private async prepareLabelGeometry(
        layer: ILayerProps,
        operateLayer?: ILayerProps | null,
        operationVersion?: number,
    ) {
        const { label } = layer.data as IMarkupLayerProps;
        const text = LabelKit.getRenderText(label);
        if (!text) {
            if (operationVersion !== undefined && this.labelOperationVersions.get(layer.id) !== operationVersion)
                return false;
            fixDrawResult(layer, operateLayer);
            return true;
        }

        await prepareMarkupLayerDocumentFonts(this.framework.service, layer);
        if (operationVersion !== undefined && this.labelOperationVersions.get(layer.id) !== operationVersion)
            return false;
        if (!fixLabelSize(layer)) return false;

        fixDrawResult(layer, operateLayer);
        return true;
    }

    /** 修正配方的图层宽高 */
    fixLayerSize(layer: ILayerProps) {
        fixMarkupLayerSize(layer);
    }

    /** 设置操作的原始数据 */
    setAttrOperateOriginData(layer: ILayerProps) {
        this.operateOriginLayer = layer;
    }

    /** 清除操作的原始数据 */
    clearAttrOperateOriginData() {
        this.operateOriginLayer = null;
    }

    /** 字内容 */
    async setLabelType(
        id: string,
        labelType: MarkupLabelType,
        labelText: string,
        labelNum: number,
        labelUnits: MarkupLabelUnit[],
        oldLayer?: ILayerProps,
        isResetActiveLayer = true,
    ) {
        const operationVersion = this.beginLabelOperation(id);
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        const label = (layer.data as IMarkupLayerProps).label;
        label.type = labelType;
        label.text = labelText;
        label.num = labelNum;
        label.units = labelUnits;
        if (!(await this.prepareLabelGeometry(layer, this.operateOriginLayer, operationVersion))) return;
        this.framework.service.frame.addOrUpdateLayer(layer);
        if (isResetActiveLayer) {
            this.framework.service.frame.setActiveLayer(layer.id);
        }
        if (oldLayer) {
            this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelType);
            updateScreenshotUrl(this.identity, layer.id);
        }
    }

    /** 字位子 */
    setLabelPosition(id: string, labelPosition: MarkupTextPositionType) {
        this.beginLabelOperation(id);
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        setLabelPosition(layer, labelPosition, this.operateOriginLayer);
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
        this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelPosition);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 字角度 */
    setLabelRotate(id: string, labelRotate: number, oldLayer?: ILayerProps) {
        this.beginLabelOperation(id);
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        setLabelRotate(layer, labelRotate, this.operateOriginLayer);
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
        if (oldLayer) {
            this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelRotate);
            updateScreenshotUrl(this.identity, layer.id);
        }
    }

    /** 字体 */
    async setLabelFontFamily(id: string, fontFamily: string) {
        const operationVersion = this.beginLabelOperation(id);
        const font = this.framework.service.font.getByFamily(fontFamily);
        const materialId = font.id;
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        const label = (layer.data as IMarkupLayerProps).label;
        label.fontFamily = fontFamily;
        const materialIds = { ...(layer.materialIds || {}) };
        if (!materialId) {
            delete materialIds[MaterialIdsType.FONT_FAMILY];
        } else {
            materialIds[MaterialIdsType.FONT_FAMILY] = [`${materialId}`];
        }
        layer.materialIds = materialIds;
        if (!(await this.prepareLabelGeometry(layer, this.operateOriginLayer, operationVersion))) return;
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
        this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelFontFamily);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 字号 */
    async setLabelFontSize(id: string, fontSize: number) {
        const operationVersion = this.beginLabelOperation(id);
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        (layer.data as IMarkupLayerProps).label.fontSize = fontSize;
        if (!(await this.prepareLabelGeometry(layer, this.operateOriginLayer, operationVersion))) return;
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
        this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelFontSize);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 字颜色 */
    setLabelColor(id: string, labelColor: string, oldLayer?: ILayerProps) {
        this.beginLabelOperation(id);
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        setLabelColor(layer, labelColor);
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
        if (oldLayer) {
            this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelColor);
            updateScreenshotUrl(this.identity, layer.id);
        }
    }

    /** 字样式 */
    async setLabelStyle(id: string, bold: boolean, italic: boolean) {
        const operationVersion = this.beginLabelOperation(id);
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        const label = (layer.data as IMarkupLayerProps).label;
        label.bold = bold;
        label.italic = italic;
        if (!(await this.prepareLabelGeometry(layer, this.operateOriginLayer, operationVersion))) return;
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
        this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelStyle);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 字背景色 */
    setLabelBackground(id: string, background: string, oldLayer?: ILayerProps) {
        this.beginLabelOperation(id);
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        setLabelBackground(layer, background, this.operateOriginLayer);
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
        if (oldLayer) {
            this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelBackground);
            updateScreenshotUrl(this.identity, layer.id);
        }
    }

    /** 线颜色 */
    setFillColor(id: string, fillColor: string, oldLayer?: ILayerProps) {
        this.beginLabelOperation(id);
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        setFillColor(layer, fillColor);
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
        if (oldLayer) {
            this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].fillColor);
            updateScreenshotUrl(this.identity, layer.id);
        }
    }

    /** 线样式 */
    setFillStyle(id: string, dasharray: MarkupDasharrayType, linecap: MarkupLinecapType) {
        this.beginLabelOperation(id);
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        setFillStyle(layer, dasharray, linecap);
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
        this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].fillStyle);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 线粗细 */
    setFillWidth(id: string, width: number, oldLayer?: ILayerProps) {
        this.beginLabelOperation(id);
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        setFillWidth(layer, width, this.operateOriginLayer);
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
        if (oldLayer) {
            this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].fillWidth);
            updateScreenshotUrl(this.identity, layer.id);
        }
    }

    /** 端点 */
    setPoint(id: string, point: IMarkupPoint, isStartPoint: boolean) {
        this.beginLabelOperation(id);
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        setPoint(layer, point, isStartPoint, this.operateOriginLayer);
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
        this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].point);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 交换端点 */
    async swapPoint(id: string) {
        this.beginLabelOperation(id);
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        const data = layer.data as IMarkupLayerProps;
        setSwap(layer, cloneDeep(data.endPoint), cloneDeep(data.startPoint));
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
        this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].swap);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 延长线 */
    async setLine(id: string, hasLine: boolean) {
        this.beginLabelOperation(id);
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        setLine(layer, hasLine);
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
        this.framework.service.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].line);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 添加预设标注 */
    async addPresetMarkup(layer: ILayerProps, options: { originSize: ISize; destSize: ISize; trackFunc?: string }) {
        const { destSize, originSize, trackFunc } = options;

        //矫正字体大小
        const ratio = destSize.width / 800;
        const data = layer.data as IMarkupLayerProps;
        data.label.fontSize = clampFontSize(data.label.fontSize * ratio);

        if (originSize && destSize) {
            this.framework.service.frame.scaleLayers([layer], originSize, destSize, false, false, true);
        }
        await prepareMarkupLayerDocumentFonts(this.framework.service, layer);
        if (!fixMarkupLayerSize(layer)) return;

        this.addByDiagonal({ layer, trackFunc });
    }
}

/**
 * 标注笔图层插件
 */
export const markupLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.MARKUP_LAYER,
    createInstance({ identity }) {
        return [{ key: 'MarkupLayer', value: new MarkupLayer(identity) }];
    },
    createBehavior() {
        return markupLayerBehavior;
    },
};
